import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIdioma } from '../../i18n/idioma';
import { useAIChat } from '../../contexts/AIChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCRM } from '../../contexts/CRMContext';
import { useEstimates } from '../../contexts/EstimatesContext';
import { useProjects } from '../../contexts/ProjectsContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useFleet } from '../../contexts/FleetContext';
import { useTimeClockCtx } from '../../contexts/TimeClockContext';
import { buildCRMContext } from '../../services/aiChat';
import { useVoiceConversation, ConversationTurnResult } from '../../hooks/useVoiceConversation';
import PlasmaOrb from './PlasmaOrb';

/**
 * Voice conversation popover.
 *
 * Just the plasma orb — no card, no backdrop, no chrome. Floats above the
 * chat input area, slides up from the bottom.
 *
 * Conversation simulation (no WebRTC):
 *   1. Open → mic auto-listens (VAD).
 *   2. User speaks → label flips to "I hear you" (visual cue).
 *      VAD watches for ~768ms of silence to detect end-of-speech automatically.
 *   3. End-of-speech → "Thinking…" → AI replies in voice → "Speaking…".
 *   4. AI finishes → back to "You can talk", listening for next turn.
 *   5. Barge-in: VAD running during playback aborts in-flight fetch and
 *      resumes listening if the user starts talking. Echo guardrail (300ms
 *      grace + >0.85 confidence + 4 consecutive frames) prevents the AI's
 *      own speakerphone audio from triggering false interruptions.
 *   6. Idle auto-close after ~18s of silence in 'listening' state.
 *   7. Tap orb (or Esc) to exit anytime.
 */

const IDLE_AUTO_CLOSE_MS = 18_000;

const VoiceConversationModal: React.FC = () => {
    const { t } = useIdioma();
  const {
    isVoiceConversationOpen,
    closeVoiceConversation,
    submitVoiceTurnStreaming,
  } = useAIChat();

  const { currentUser, userRole } = useAuth();
  const { leads, clients, appointments } = useCRM();
  const { estimates, pricebook, serviceTemplates } = useEstimates();
  const { projects } = useProjects();
  const { currentView } = useNavigation();
  const { settings, metrics } = useSettings();
  const { equipment } = useFleet();
  const { crews, teamMembers, activeTimeLogs } = useTimeClockCtx();

  const buildContext = useCallback(() => buildCRMContext({
    userName: currentUser?.displayName || 'User',
    userRole: userRole || 'Admin',
    currentView,
    settings,
    metrics,
    clients,
    estimates,
    projects,
    leads,
    appointments,
    pricebook,
    serviceTemplates,
    equipment,
    crews,
    teamMembers,
    activeTimeLogs,
  }), [
    currentUser?.displayName, userRole, currentView, settings, metrics,
    clients, estimates, projects, leads, appointments,
    pricebook, serviceTemplates, equipment, crews, teamMembers, activeTimeLogs,
  ]);

  const onTurn = useCallback(async (
    audioBlob: Blob,
    signal: AbortSignal,
  ): Promise<ConversationTurnResult> => {
    // Streaming flow (Phase 3): voice-ai-stream emits Gemini tokens as SSE,
    // sentenceStreamPlayer detects sentence boundaries and dispatches each
    // to the TTS server in order. First audio plays when sentence #1 is
    // ready, not when the whole reply is.
    //
    // We expose an `audioStarted` deferred so the conversation hook can
    // gate its barge-in detection AND its 'Thinking…' label on actual
    // playback start instead of stream-result arrival.
    let resolveAudioStarted: () => void = () => {};
    const audioStarted = new Promise<void>((resolve) => {
      resolveAudioStarted = resolve;
    });

    let lastTranscript = '';
    let lastReply = '';

    const finished = submitVoiceTurnStreaming(audioBlob, buildContext(), {
      signal,
      onTranscript: (t) => { lastTranscript = t; },
      onReplyText: (full) => { lastReply = full; },
      onFirstAudio: (latencyMs) => {
        console.debug(`[VoiceConversationModal] first audio in ${latencyMs.toFixed(0)}ms`);
        resolveAudioStarted();
      },
    }).then((result) => {
      lastTranscript = result.transcript || lastTranscript;
      lastReply = result.reply || lastReply;
      // Defensive: unblock the hook even if onFirstAudio never fired
      // (e.g. error path or zero-length reply).
      resolveAudioStarted();
      if (result.error && result.error !== 'aborted') {
        throw new Error(result.error);
      }
    });

    return {
      kind: 'audio-stream',
      finished,
      audioStarted,
      transcript: lastTranscript,
      reply: lastReply,
    };
  }, [submitVoiceTurnStreaming, buildContext]);

  const { state, amplitude, isUserSpeaking, isAudioPlaying, errorMessage, start, stop, isSupported } = useVoiceConversation({ onTurn });

  // Auto-start the loop when popover opens; tear down on close/unmount.
  useEffect(() => {
    if (!isVoiceConversationOpen) return;
    start();
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVoiceConversationOpen]);

  // Esc closes.
  useEffect(() => {
    if (!isVoiceConversationOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeVoiceConversation(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isVoiceConversationOpen, closeVoiceConversation]);

  // Anchor the orb above the chat input element. The chat content is offset
  // by the app's left sidebar (and again by the conversation list inside
  // AIChatView), so a viewport-centered orb ends up off to the left. We track
  // the input's bounding rect on open + window changes so the orb stays in
  // sync if the layout shifts (sidebar collapse, window resize, etc.).
  const [anchorPos, setAnchorPos] = useState<{ left: number; bottom: number } | null>(null);
  useEffect(() => {
    if (!isVoiceConversationOpen) return;
    const updatePos = () => {
      const el = document.getElementById('ai-chat-input-anchor');
      if (!el) {
        setAnchorPos(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      setAnchorPos({
        left: rect.left + rect.width / 2,
        bottom: window.innerHeight - rect.top + 16, // 16px gap above input
      });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    // Re-poll briefly to catch layouts that settle after first paint.
    const intervalId = setInterval(updatePos, 400);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
      clearInterval(intervalId);
    };
  }, [isVoiceConversationOpen]);

  // Idle auto-close: only counts while we're in 'listening' state with no speech.
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (!isVoiceConversationOpen) return;
    if (state !== 'listening') return;
    if (isUserSpeaking) return; // don't tick idle timer while they're talking
    idleTimerRef.current = setTimeout(() => {
      closeVoiceConversation();
    }, IDLE_AUTO_CLOSE_MS);
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [state, isUserSpeaking, isVoiceConversationOpen, closeVoiceConversation]);

  // Label uses sustained `isUserSpeaking` (debounced) instead of raw amplitude
  // so it doesn't ping-pong during natural speech bursts. We keep "Thinking…"
  // visible through the entire STT → LLM → TTS-synthesis window, even after
  // `state` flips to 'speaking', because audio doesn't actually start playing
  // until the worklet/buffer-source emits its first sample. Switching to
  // "Tap to stop" while the user is still hearing silence feels broken.
  const orbLabel = useMemo(() => {
    if (state === 'thinking') return 'Thinking…';
    if (state === 'speaking' && !isAudioPlaying) return 'Thinking…';
    if (state === 'speaking') return t('voz.orbe.parar');
    if (state === 'listening' && isUserSpeaking) return t('voz.orbe.escucho');
    return t('voz.orbe.hablar');
  }, [state, isAudioPlaying, isUserSpeaking]);

  // The orb's animation is keyed on `state`. We override to 'thinking' while
  // audio is being synthesized so the visual matches the label — otherwise
  // the orb runs the speaking animation while the user is still waiting.
  const effectiveOrbState = useMemo(() => {
    if (state === 'speaking' && !isAudioPlaying) return 'thinking' as const;
    return state;
  }, [state, isAudioPlaying]);

  if (!isVoiceConversationOpen) return null;

  // VAD couldn't initialize (e.g. WASM failed to load). Surface a tiny error
  // bubble below the orb instead of a full card so the visual stays minimal.
  const showError = !!errorMessage && errorMessage !== 'aborted';

  // When we've found the input anchor, override CSS defaults with inline
  // positioning. Keep `transform` so the slide-up keyframe still works.
  const popoverStyle: React.CSSProperties | undefined = anchorPos
    ? { left: `${anchorPos.left}px`, bottom: `${anchorPos.bottom}px` }
    : undefined;

  return (
    <div className="voice-popover" style={popoverStyle} role="status" aria-live="polite">
      {!isSupported ? (
        <div className="bg-navy-850 text-white text-xs rounded-xl px-4 py-2 shadow-s2">
          {t('voz.error.sinmicrofono')}
        </div>
      ) : (
        <PlasmaOrb
          state={effectiveOrbState}
          amplitude={amplitude}
          label={orbLabel}
          size="sm"
          onClick={() => { stop(); closeVoiceConversation(); }}
          ariaLabel={t('voz.orbe.arialabel', { effectiveOrbState })}
        />
      )}

      {showError && (
        <div className="text-[11px] text-white bg-peligro rounded-xl px-3 py-1.5 max-w-[320px] text-center shadow-s2">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default VoiceConversationModal;
